/*
 * product-card.js — shared card interactions (delegated, works for every .cc-card).
 * Swatch hover/click: select that color's variant → swap the card image (data-image, from the
 * variant's custom.variant_images), update the card link + price + ship + active swatch.
 * Wishlist heart is handled by wishlist.js.
 */
(function () {
  'use strict';

  // Wishlist page locks each card to its saved variant (rendered server-side via ?variant=epi)
  // — block USER-driven colour switching here.
  function isLocked(sw) { return !!(sw.closest && sw.closest('.wishlist-grid')); }

  function selectSwatch(sw) {
    if (!sw) return;
    var url = sw.getAttribute('data-url');
    var card = sw.closest('.cc-card');
    if (card && url) {
      card.querySelectorAll('.cc-card__image-link, .cc-card__title-link').forEach(function (a) {
        a.setAttribute('href', url);
      });
      if (card.tagName === 'A') card.setAttribute('href', url);
    }
    // Swap the card image to the selected colour's variant image (sticky — stays on mouseleave).
    var img = sw.getAttribute('data-image');
    if (card && img) {
      // Specifically target .cc-card__image.is-main or .cc-card__image
      var imgEl = card.querySelector('.cc-card__image.is-main, .cc-card__image');
      if (imgEl && imgEl.tagName === 'IMG') {
        imgEl.removeAttribute('srcset');
        imgEl.srcset = '';
        imgEl.removeAttribute('sizes');
        imgEl.removeAttribute('loading');
        imgEl.setAttribute('src', img);
        imgEl.src = img;
      }
    }
    // Price + compare follow the selected variant (per-seat, computed server-side).
    if (card) {
      var price = sw.getAttribute('data-price');
      if (price) { var pEl = card.querySelector('.cc-card__price'); if (pEl) pEl.textContent = price; }
      var cEl = card.querySelector('.cc-card__compare-price');
      if (cEl && sw.hasAttribute('data-compare')) cEl.textContent = sw.getAttribute('data-compare') || '';
      // Ship time follows the selected colour's variant (custom.ship_time).
      var ship = sw.getAttribute('data-ship');
      var shipsEl = card.querySelector('.cc-card__ships');
      if (shipsEl && sw.hasAttribute('data-ship')) {
        var tEl = shipsEl.querySelector('.cc-card__ship-text');
        if (tEl) tEl.textContent = ship || '';
        shipsEl.style.display = ship ? '' : 'none';
      }
    }
    var group = sw.parentNode;
    if (group) {
      group.querySelectorAll('.cc-card__swatch--active').forEach(function (s) {
        s.classList.remove('cc-card__swatch--active');
      });
    }
    sw.classList.add('cc-card__swatch--active');

    if (card) {
      var heart = card.querySelector('.js-wishlist-btn');
      var variantId = sw.getAttribute('data-variant-id');
      if (heart && variantId) {
        heart.setAttribute('data-epi', variantId);
        heart.dispatchEvent(new CustomEvent('product-card:variant-change', {
          bubbles: true,
          detail: { variantId: variantId }
        }));
      }
    }
  }

  // Preload all variant images for a product card on first hover/mouseover over .cc-card
  function preloadCardImages(card, fetchPriority) {
    if (!card || card.dataset.preloaded) return;
    card.dataset.preloaded = 'true';
    var swatches = card.querySelectorAll('.cc-card__swatch[data-image]');
    var seen = {};
    swatches.forEach(function (sw) {
      var imgUrl = sw.getAttribute('data-image');
      if (imgUrl && !seen[imgUrl]) {
        seen[imgUrl] = true;
        var img = new Image();
        if (fetchPriority && 'fetchPriority' in img) img.fetchPriority = fetchPriority;
        img.decoding = 'async';
        img.src = imgUrl;
      }
    });
  }

  // Touch screens have no reliable hover phase before a swatch tap. Warm a card's
  // variant-image cache just before it enters view instead of attaching a scroll
  // handler that runs for every frame. The narrow root margin limits this to the
  // next row of cards; slow/data-saving connections retain interaction-only loading.
  var mobileCardPreloadObserver;
  function canPreloadMobileCards() {
    if (!window.matchMedia || !window.matchMedia('(max-width: 749px)').matches) return false;
    var connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    return !(connection && (connection.saveData || /(^|-)2g/.test(connection.effectiveType || '')));
  }

  function observeMobileCardPreloads() {
    if (!canPreloadMobileCards() || !('IntersectionObserver' in window)) return;
    if (!mobileCardPreloadObserver) {
      mobileCardPreloadObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          mobileCardPreloadObserver.unobserve(entry.target);
          preloadCardImages(entry.target, 'low');
        });
      }, { rootMargin: '0px 0px 200px 0px' });
    }
    document.querySelectorAll('.cc-card').forEach(function (card) {
      if (card.dataset.preloaded || card.dataset.mobilePreloadObserved) return;
      card.dataset.mobilePreloadObserved = 'true';
      mobileCardPreloadObserver.observe(card);
    });
  }

  // Preload variant images as soon as user hovers anywhere on the card
  ['mouseover', 'pointerover'].forEach(function (evt) {
    document.addEventListener(evt, function (e) {
      var card = e.target.closest ? e.target.closest('.cc-card') : null;
      if (card) preloadCardImages(card);
    });
  });

  // Hover selects the variant (active + card link follow the hovered swatch).
  ['mouseover', 'pointerover', 'mouseenter'].forEach(function (evt) {
    document.addEventListener(evt, function (e) {
      var sw = e.target.closest ? e.target.closest('.cc-card__swatch') : null;
      if (!sw || isLocked(sw)) return;
      selectSwatch(sw);
    });
  });

  // Click on a swatch selects the variant instead of navigating (touch / no-hover).
  document.addEventListener('click', function (e) {
    var sw = e.target.closest ? e.target.closest('.cc-card__swatch') : null;
    if (!sw || isLocked(sw)) return;
    e.preventDefault();
    e.stopPropagation();
    selectSwatch(sw);
  });

  // Keyboard a11y (swatches are role="button" tabindex="0").
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    var sw = e.target.closest ? e.target.closest('.cc-card__swatch[data-variant-id]') : null;
    if (!sw || isLocked(sw)) return;
    e.preventDefault();
    selectSwatch(sw);
  });

  // ---- Swatch thumbnails: product-card.liquid paints them inline only on the eager (first) card; the
  //      rest carry data-bg and get their background-image once the card is near the viewport. The
  //      generous horizontal margin covers carousels, whose off-screen cards sit beside the viewport. ----
  var swatchBgObserver;
  function applySwatchBgs(card) {
    card.querySelectorAll('.cc-card__swatch[data-bg]').forEach(function (s) {
      s.style.backgroundImage = 'url(' + s.getAttribute('data-bg') + ')';
      s.removeAttribute('data-bg');
    });
  }
  function observeSwatchBgs() {
    var cards = [];
    document.querySelectorAll('.cc-card__swatch[data-bg]').forEach(function (s) {
      var card = s.closest('.cc-card');
      if (card && !card.dataset.swatchBgObserved) { card.dataset.swatchBgObserved = 'true'; cards.push(card); }
    });
    if (!cards.length) return;
    if (!('IntersectionObserver' in window)) { cards.forEach(applySwatchBgs); return; }
    if (!swatchBgObserver) {
      swatchBgObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          swatchBgObserver.unobserve(entry.target);
          applySwatchBgs(entry.target);
        });
      }, { rootMargin: '400px 100%' });
    }
    cards.forEach(function (card) { swatchBgObserver.observe(card); });
  }

  // ---- Swatch overflow: show as many swatches as fit one row; rest -> "+N" (computed, not a fixed limit) ----
  // Batched across every card — all writes, then all reads, then all writes — so the browser lays the page
  // out a couple of times in total instead of several times per card (Lighthouse "forced reflow").
  var textCtx;
  function textWidth(font, text) {
    if (!textCtx) textCtx = document.createElement('canvas').getContext('2d');
    textCtx.font = font;
    return textCtx.measureText(text).width;
  }

  function layoutAllSwatches() {
    var rows = [];
    document.querySelectorAll('.cc-card__swatches').forEach(function (container) {
      var swatches = Array.prototype.slice.call(container.querySelectorAll('.cc-card__swatch'));
      if (swatches.length) rows.push({ c: container, sw: swatches, chip: container.querySelector('.cc-card__swatch-more') });
    });
    if (!rows.length) return;

    // 1. write: every swatch shown, the chip shown too so its padding/border width can be read
    rows.forEach(function (r) {
      r.sw.forEach(function (s) { s.style.display = ''; });
      if (r.chip) { r.chip.style.display = ''; r.chip.textContent = '+' + r.sw.length; }
    });

    // 2. read: row edge, swatch edges, chip width + spacing (the chip follows the last swatch, so
    //    showing it does not move any swatch)
    rows.forEach(function (r) {
      var cs = window.getComputedStyle(r.c);
      r.c._swW = r.c.clientWidth;
      r.edge = r.c.getBoundingClientRect().right - (parseFloat(cs.paddingRight) || 0) + 0.5;
      r.gap = parseFloat(cs.columnGap) || 0;
      r.rights = r.sw.map(function (s) { return s.getBoundingClientRect().right; });
      if (r.chip) {
        var chipCs = window.getComputedStyle(r.chip);
        r.chipML = parseFloat(chipCs.marginLeft) || 0;
        r.chipFont = chipCs.font;
        // chip width = its fixed part (padding/border) + the width of its "+N" text, which differs per
        // digit in this font ("+1" is narrower than "+9"), so the text is measured for each candidate N
        r.chipBase = r.chip.getBoundingClientRect().width - textWidth(r.chipFont, r.chip.textContent);
      }
    });

    // 3. write: same rule as before — keep the swatches that fit, then drop more until "+N" fits too
    rows.forEach(function (r) {
      var n = r.sw.length, fit = 0;
      while (fit < n && r.rights[fit] <= r.edge) fit++;
      r.visible = n;
      if (fit >= n || !r.chip) { if (r.chip) r.chip.style.display = 'none'; return; }
      var visible = fit;
      while (visible > 0 && r.rights[visible - 1] + r.gap + r.chipML + r.chipBase + textWidth(r.chipFont, '+' + (n - visible)) > r.edge) visible--;
      for (var k = visible; k < n; k++) r.sw[k].style.display = 'none';
      r.chip.textContent = '+' + (n - visible);
      r.visible = visible;
    });

    // 4. read once more to confirm the chip fits with its real text; rarely needed, falls back to
    //    the original one-by-one trim for that row only
    rows.forEach(function (r) {
      if (!r.chip || r.visible >= r.sw.length) return;
      if (r.chip.getBoundingClientRect().right <= r.edge) return;
      var visible = r.visible, guard = 0;
      while (visible > 0 && r.chip.getBoundingClientRect().right > r.edge && guard < 30) {
        visible--;
        r.sw[visible].style.display = 'none';
        r.chip.textContent = '+' + (r.sw.length - visible);
        guard++;
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      observeSwatchBgs();
      requestAnimationFrame(function () {
        layoutAllSwatches();
        observeMobileCardPreloads();
      });
    });
  } else {
    observeSwatchBgs();
    requestAnimationFrame(function () {
      layoutAllSwatches();
      observeMobileCardPreloads();
    });
  }
  // Re-run after load only if a row's width changed since the first pass (e.g. late CSS); otherwise
  // the first pass already holds and a second full pass would just repeat the layout work.
  window.addEventListener('load', function () {
    var changed = false;
    document.querySelectorAll('.cc-card__swatches').forEach(function (c) {
      var w = c.clientWidth;
      if (c._swW !== undefined && c._swW !== w) changed = true;
      c._swW = w;
    });
    if (changed) layoutAllSwatches();
    observeMobileCardPreloads();
  });
  var _swRT;
  window.addEventListener('resize', function () {
    clearTimeout(_swRT);
    _swRT = setTimeout(layoutAllSwatches, 150);
  });

  // Async sections inject their cards AFTER load/DOMContentLoaded have fired, so
  // those swatch rows never get trimmed and overflow. Watch the known async card
  // hosts and re-run the layout when cards land:
  //   <product-recommendations>   — Shopify Recommendations API (innerHTML swap)
  //   [data-facet-grid]            — collection/search page (cgrid-facets AJAX filter updates)
  //   #wishlist-items-container    — wishlist page (per-item Section Rendering fetch,
  //                                  cards appended into nested slots → needs subtree)
  // The rAF flag coalesces the burst of mutations into a single layout pass.
  // (layoutSwatchOverflow only touches inline `style`, not childList, so it can't
  //  retrigger the observer → no loop.)
  var _swPending = false;
  function scheduleSwatchLayout() {
    if (_swPending) return;
    _swPending = true;
    requestAnimationFrame(function () {
      _swPending = false;
      observeSwatchBgs();
      layoutAllSwatches();
      observeMobileCardPreloads();
    });
  }
  document.querySelectorAll('product-recommendations, [data-facet-grid], #wishlist-items-container').forEach(function (el) {
    new MutationObserver(scheduleSwatchLayout).observe(el, { childList: true, subtree: true });
  });
})();
